import queryString from "query-string";
import baseURL from "./settings";
import axios from "axios";
import { runInBatches } from "./utils/batchPromises";
import { SignetSignetInlayResources, SignetSignetPricingDataResources } from "./components/Signet/util";
import { rowsToApiTags } from "./components/TrainingVideos/util";

export const apiUrl = baseURL.apiBaseUrl;

export const rawFileExtraction = async data => {
  let form_data = new FormData();

  for (let key in data) {
    if (data.hasOwnProperty(key)) {
      let value = (!data[key] && typeof data[key] !== 'number' && typeof data[key] !== 'boolean') ? "" : data[key];
      if (value && value.hasOwnProperty('rawFile') && key === "icon_image_url") {
        const converted = await convertFileToBase64(value);
        form_data.append(key, converted);
      } else if (value && value.hasOwnProperty('rawFile')) {
        form_data.append(key, value.rawFile)
      } else if (Array.isArray(data[key])) {
        for (let i = 0; i < value.length; i++) {
          form_data.append(`${key}[${i}]`, value[i]);
        }
      } else {
        form_data.append(key, value);
      }
    }
  }

  return form_data;
};

export const convertFileToBase64 = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;

    reader.readAsDataURL(file.rawFile);
  });

const STORE_PRICE_MODEL_KEYS = new Set([
  "retail_price_model",
  "precious_retail_price_model",
  "signet_retail_price_model",
  "signet_precious_retail_price_model",
]);

const storeLogoFileFromValue = value => {
  if (!value) return null;
  if (Array.isArray(value) && value.length > 0) {
    return storeLogoFileFromValue(value[0]);
  }
  if (value instanceof File) return value;
  if (value.rawFile instanceof File) return value.rawFile;
  return null;
};

const shouldRemoveStoreLogo = value => {
  if (value === undefined) return false;
  if (value === null || value === "" || value === false) return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
};

const buildStoreFormData = (data, { logoFile, removeStoreLogo = false } = {}) => {
  const formData = new FormData();

  Object.keys(data).forEach(key => {
    if (key === "id" || key === "store_logo") return;

    const val = data[key];
    if (val === undefined) return;

    if (STORE_PRICE_MODEL_KEYS.has(key)) {
      formData.append(key, JSON.stringify(val ?? []));
      return;
    }

    if (typeof val === "boolean") {
      formData.append(key, val ? "1" : "0");
      return;
    }

    if (val === null) {
      formData.append(key, "");
      return;
    }

    if (typeof val === "object") {
      formData.append(key, JSON.stringify(val));
      return;
    }

    formData.append(key, val);
  });

  if (logoFile) {
    formData.append("store_logo", logoFile);
  }
  if (removeStoreLogo) {
    formData.append("remove_store_logo", "1");
  }

  return formData;
};

const dataProvider = {
  getList: async (resource, params) => {
    const { page, perPage } = params.pagination;
    //pagination
    const query = {
      "page[number]": page,
      "page[size]": perPage
    };
    //filter
    Object.keys(params.filter || {}).forEach(key => {
      if (resource === 'position' && key === 'category_id') {
        query[`category_id`] = params.filter[key];
      } else if (key === 'deleted_at') {
        query[`filter[trashed]`] = params.filter[key];
      } else {
        query[`filter[${key}]`] = params.filter[key];
      }
    });

    //sort
    if (resource === "netsuite/inlay") {
      query.sort = "inlay_id";
    } else if (resource === "netsuite/sleeve") {
      query.sort = "sleeve_id";
    } else  if (resource === "ring-material-sort-order"){
        // Default sort by sort_order when no sort is specified
        query.sort = "sort_order";
    } else if (resource === "netsuite/design-pattern") {
    //   query.sort = "machine_pattern_id";
    } else if (params.sort && params.sort.field && (resource !== 'orders' || (resource === 'orders' && params.sort.field !== 'id'))) {
      const prefix = params.sort.order === "ASC" ? "" : "-";
      query.sort = `${prefix}${params.sort.field}`;
    }

    const url =
      resource === "products/categories" || resource === "precious-price"
        ? `${apiUrl}/${resource}`
        : `${apiUrl}/${resource}?${queryString.stringify(query)}`;
    return await axios.get(url).then(({ data }) => {
      // Handle cases where data.data might be undefined
      const responseData = data.data || [];
      // this changes the keys to include the product type when displaying a list of both static and dynamic products
      let updatedData;
      if (resource === 'position' && Array.isArray(responseData)) {
        updatedData = responseData.map(resource => ({ ...resource, id: `${resource.id}_${resource.type}` }));
      } else if (resource === "netsuite/inlay" && Array.isArray(responseData)) {
        // Map inlay_id to id for netsuite/inlay
        updatedData = responseData.map((item) => ({
          ...item,
          id: item.inlay_id || item.id,
        }));
      } else if (resource === "netsuite/sleeve" && Array.isArray(responseData)) {
        // Map sleeve_id to id for netsuite/sleeve
        updatedData = responseData.map((item) => ({
          ...item,
          id: item.sleeve_id || item.id,
        }));
      } else if (resource === "ring-material-sort-order" && Array.isArray(responseData)) {
        // Map sku_code or existing id to id for ring-material-sort-order
        updatedData = responseData.map((item, index) => ({
          ...item,
          id: item.id || item.sku_code || `sort-order-${index}`,
        }));
      } else if (resource === "netsuite/ring-finish" && Array.isArray(responseData)) {
        // Ensure each item has an id (API returns first_ring_finish, second_ring_finish, accent_ring_finish, netsuite_id, netsuite_value)
        updatedData = responseData.map((item, index) => ({
          ...item,
          id: item.id ?? item.netsuite_id ?? index,
        }));
      } else if (resource === "netsuite/inlay-finish" && Array.isArray(responseData)) {
        // Ensure each item has an id (API returns first_inlay_finish, second_inlay_finish, netsuite_id, netsuite_value)
        updatedData = responseData.map((item, index) => ({
          ...item,
          id: item.id ?? item.netsuite_id ?? index,
        }));
      } else if (resource === "precious-price" && Array.isArray(responseData)) {
        // API returns precious_price_id, not id; cast can be boolean
        updatedData = responseData.map((item) => ({
          ...item,
          id: item.precious_price_id ?? item.id,
        }));
      } else {
        updatedData = responseData;
      }
      return {
        data: Array.isArray(updatedData) ? updatedData : [],
        total: data.meta ? data.meta.pagination.total : (Array.isArray(responseData) ? responseData.length : 0),
      };
    }).catch((error) => {
      throw error;
    });
  },

  getOne: async (resource, params) => {
    // Handle "create" ID for netsuite/design-pattern - return empty object for create forms
    if (resource === "netsuite/design-pattern" && (params.id === "create" || params.id === "")) {
      return {
        data: { id: "" }
      };
    }

    const url =
      resource === "me" || resource === "ring/options" || resource === "website/metrics"
        ? `${apiUrl}/${resource}`
        : `${apiUrl}/${resource}/${params.id}`;

    return await axios.get(url).then(({ data: { data: response } }) => {
      let updatedResponse;
      if (resource === "netsuite/inlay") {
        // Map inlay_id to id for netsuite/inlay
        updatedResponse = response.inlay_id ? { ...response, id: response.inlay_id } : { ...response, id: response.id || 1 };
      } else if (resource === "netsuite/ring-finish") {
        // Ensure an id for netsuite/ring-finish
        updatedResponse = response.id ? response : { ...response, id: response.netsuite_id || 1 };
      } else if (resource === "netsuite/inlay-finish") {
        // Ensure an id for netsuite/inlay-finish
        updatedResponse = response.id ? response : { ...response, id: response.netsuite_id || 1 };
      } else if (resource === "netsuite/sleeve") {
        // Map sleeve_id to id for netsuite/sleeve
        updatedResponse = response.sleeve_id ? { ...response, id: response.sleeve_id } : { ...response, id: response.id || 1 };
      } else if (resource === "precious-price") {
        // API returns precious_price_id, not id
        updatedResponse = response.precious_price_id != null
          ? { ...response, id: response.precious_price_id }
          : { ...response, id: response.id || 1 };
      } else {
        updatedResponse = response.id ? response : { ...response, id: 1 };
      }
      return {
        data: updatedResponse
      };
    }).catch((error) => {
      // If it's a 404 or other error for create page, return empty data
      if (error.response && error.response.status === 404 && params.id === "create") {
        return {
          data: { id: "" }
        };
      }
      throw error;
    });
  },

  getMany: async (resource, params) => {
    const url = `${apiUrl}/${resource}?filter[id]=${params.ids}`;
    return await axios.get(url).then(({ data }) => {
      return {
        data: data.data,
        total: data.meta ? data.meta.pagination.total : '',
      };
    });
  },

  getManyReference: async (resource, params) => {
    // const { page, perPage } = params.pagination;
    // const { field, order } = params.sort;
    // const query = {
    //   sort: JSON.stringify([field, order]),
    //   range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
    //   filter: JSON.stringify({
    //     ...params.filter,
    //     [params.target]: params.id
    //   })
    // };

    const url = `${apiUrl}/${resource}`;
    return await axios.get(url).then(({ data: response, headers }) => {
      const data = response[resource];
      return {
        data,
        total: headers["content-range"]
      };
    });
  },

  update: async (resource, params) => {
    if (resource === "shipping" || resource === "account/shipping") {
      const cost = Number(params.data.cost.replace(/[^0-9.]+/g, ""));
      return await axios
        .patch(`${apiUrl}/${resource}/${params.id}`, { cost })
        .then(({ data }) => ({
          data
        }));
    }

    if (resource === "position") {
      return await axios
        .patch(`${apiUrl}/${resource}`, params.data)
        .then(({ data }) => ({
          data
        }));
    }

    if (resource === "accounts/restore") {
      return await axios
        .patch(`${apiUrl}/accounts/${params.id}/restore`)
        .then(({ data }) => ({
          data
        }));
    }

    if (resource === "stores/restore") {
      return await axios
        .patch(`${apiUrl}/stores/${params.id}/restore`)
        .then(({ data }) => ({
          data
        }));
    }



    if (resource === "stores") {
      const { parent_store_id, is_parent_store } = params.data;
      const parentStoreId = is_parent_store ? null : parent_store_id;
      const updatedData = { ...params.data, parent_store_id: parentStoreId };
      const logoFile = storeLogoFileFromValue(updatedData.store_logo);
      const removeStoreLogo =
        !logoFile && shouldRemoveStoreLogo(updatedData.store_logo);
      const formData = buildStoreFormData(updatedData, {
        logoFile,
        removeStoreLogo,
      });
      formData.append("_method", "PUT");
      // Same URL as PUT; POST is required for multipart so PHP populates $_POST/$_FILES.
      return await axios
        .post(`${apiUrl}/${resource}/${params.id}`, formData)
        .then(({ data: res }) => {
          const store = res?.data ?? res;
          return {
            data: {
              ...store,
              id: store?.id ?? params.id,
            },
          };
        });
    }

    if (resource === SignetSignetPricingDataResources) { // modify the data before sending to the server
      if (![0, 2].includes(params.data.attribute_id)) params.data.attribute_id = params.data.inlay_type_id;
      delete params.data.attribute_type_name;
      delete params.data.size;
      delete params.data.size_name;
      delete params.data.inlay;
      delete params.data.ring_material_name;
      delete params.data.inlay_type_id;
      delete params.data.attribute_name;
      if (params.data.attribute_id === 0 ) params.data.attribute_id = null;
    }

    if (resource === SignetSignetInlayResources) { // modify the data before sending to the server
      delete params.data.inlay_type_name;
      delete params.data.option_category_name;
    }

    if (resource === "netsuite/design-pattern") { // modify the data before sending to the server
      // Convert netsuite_id to string
      if (params.data.netsuite_id !== undefined && params.data.netsuite_id !== null) {
        params.data.netsuite_id = String(params.data.netsuite_id);
      }
    }

    if (resource === "netsuite/ring-finish" || resource === "netsuite/inlay-finish") {
      // backend expects netsuite_id as string
      if (params.data.netsuite_id !== undefined && params.data.netsuite_id !== null) {
        params.data.netsuite_id = String(params.data.netsuite_id);
      }
    }

    if (resource === "training-videos") {
      params.data = {
        ...params.data,
        tags: rowsToApiTags(params.data.tags),
      };
    }

    return await axios
      .put(`${apiUrl}/${resource}/${params.id}`, params.data)
      .then(({ data }) => {
        if (resource === "netsuite/inlay") {
          // Map inlay_id to id for netsuite/inlay
          const responseData = data.inlay_id ? { ...data, id: data.inlay_id } : { id: '', ...data };
          return { data: responseData };
        }
        if (resource === "netsuite/sleeve") {
          // Map sleeve_id to id for netsuite/sleeve
          const responseData = data.sleeve_id ? { ...data, id: data.sleeve_id } : { id: '', ...data };
          return { data: responseData };
        }
        return { data: { id: '', ...data } };
      })
      .catch(error => {
        if (
          (resource === "netsuite/ring-finish" || resource === "netsuite/inlay-finish") &&
          error &&
          error.response &&
          error.response.status === 409
        ) {
          const backendMessage = error.response.data && error.response.data.message;
          if (backendMessage) {
            const err = new Error(backendMessage);
            err.status = error.response.status;
            throw err;
          }
        }
        throw error;
      });
  },

  updateMany: async (resource, params) => {
    if (resource === "accounts/restore") {
      const responses = await runInBatches(params.ids, 10, id =>
        axios.patch(`${apiUrl}/accounts/${id}/restore`)
      );
      return { data: responses.map(response => response.data) };
    }

    if (resource === "stores/restore") {
      const responses = await runInBatches(params.ids, 10, id =>
        axios.patch(`${apiUrl}/stores/${id}/restore`)
      );
      return { data: responses.map(response => response.data) };
    }

    if (resource === "admin/orders/sync") {
      return await axios
        .post(`${apiUrl}/${resource}`, { orders: params.orders })
        .then(() => ({
          data: params.orders
        }));
    }

    const responses = await runInBatches(params.ids, 10, id =>
      axios.patch(`${apiUrl}/${resource}/${id}`)
    );
    return { data: responses.map(response => response.data) };
  },

  create: async (resource, data) => {
    if (resource === "stores") {
      const { parent_store_id, is_parent_store } = data.data;
      const parentStoreId = is_parent_store ? null : parent_store_id;
      const updatedData = { ...data.data, parent_store_id: parentStoreId };
      const logoFile = storeLogoFileFromValue(updatedData.store_logo);
      const formData = buildStoreFormData(updatedData, { logoFile });
      return await axios
        .post(`${apiUrl}/${resource}`, formData)
        .then(({ data: res }) => {
          const store = res?.data ?? res;
          return {
            data: {
              ...store,
              id: store?.id ?? "",
            },
          };
        });
    }
    if (resource === SignetSignetPricingDataResources) { // modify the data before sending to the server
      delete data.data.attribute_type_name;
      delete data.data.ring_material_name;
      delete data.data.Inlay;
      delete data.data.size_name;
      if (data.data.attribute_id === 0 ) data.data.attribute_id = null;
    }
    if (resource === SignetSignetInlayResources) { // modify the data before sending to the server
      delete data.data.inlay_type_name;
      delete data.data.option_category_name;
    }

    if (resource === "netsuite/design-pattern") { // modify the data before sending to the server
      // Convert netsuite_id to string
      if (data.data.netsuite_id !== undefined && data.data.netsuite_id !== null) {
        data.data.netsuite_id = String(data.data.netsuite_id);
      }
    }

    if (resource === "netsuite/ring-finish" || resource === "netsuite/inlay-finish") {
      // backend expects netsuite_id as string
      if (data.data.netsuite_id !== undefined && data.data.netsuite_id !== null) {
        data.data.netsuite_id = String(data.data.netsuite_id);
      }
    }

    if (resource === "training-videos") {
      data.data = {
        ...data.data,
        tags: rowsToApiTags(data.data.tags),
      };
    }

    return await axios
      .post(`${apiUrl}/${resource}`, data.data)
      .then(({ data: resData }) => {
        if (resource === "precious-price" && resData.precious_price_id != null) {
          return { data: { ...resData, id: resData.precious_price_id } };
        }
        return { data: { id: '', ...resData } };
      })
      .catch(error => {
        if (
          (resource === "netsuite/ring-finish" || resource === "netsuite/inlay-finish") &&
          error &&
          error.response &&
          error.response.status === 409
        ) {
          const backendMessage = error.response.data && error.response.data.message;
          if (backendMessage) {
            const err = new Error(backendMessage);
            err.status = error.response.status;
            throw err;
          }
        }
        throw error;
      });
  },

  delete: async (resource, params) =>
    await axios
      .delete(`${apiUrl}/${resource}/${params.id}`)
      .then(({ data }) => ({ data: { id: '', message: 'Deleted Successfully' } })),

  deleteMany: async (resource, params) => {
    await runInBatches(params.ids, 10, id => axios.delete(`${apiUrl}/${resource}/${id}`));
    return { data: [{ id: '', message: 'Deleted Successfully' }] };
  }
};

export default dataProvider;